import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Mail, Key, FileText, Clock, User, MapPin, AlertCircle, CheckCircle, MessageSquare, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const reportTypeLabels = {
  // ISO 37001 aligned categories
  'corruzione_frode_riciclaggio': 'Corruzione / Frode / Riciclaggio di denaro',
  'violazione_231_codice_etico': 'Violazione del Modello 231, Codice Etico o Codice di Condotta',
  'molestie_mobbing': 'Molestie / Mobbing / Bullismo / Comportamenti inappropriati',
  'inosservanza_normative': 'Inosservanza di normative, leggi o procedure aziendali interne',
  'abuso_informazioni_riservate': 'Abuso di informazioni riservate o dati aziendali',
  'altra_condotta_illecita': 'Altra condotta illecita o non etica',
  'contro_odv': 'Segnalazione riguardante l\'OdV',
  // Historical values (kept so old reports stay readable)
  'corruzione': 'Corruzione',
  'illeciti': 'Illeciti Amministrativi',
  'discriminazione': 'Discriminazione',
  'mobbing': 'Mobbing o Molestie',
  'sicurezza': 'Violazioni Sicurezza',
  'ambiente': 'Danno Ambientale',
  'altro': 'Altro'
};

const roleLabels = {
  'dipendente_autonomo': 'Lavoratore dipendente / autonomo',
  'collaboratore_consulente': 'Collaboratore / Libero professionista / Consulente',
  'fornitore': 'Fornitore di beni o servizi',
  'tirocinante_volontario': 'Tirocinante / Volontario',
  'socio_organi': 'Socio / Componente organi di amministrazione, direzione o controllo'
};

const statusLabels = {
  'ricevuta': { label: 'Ricevuta', color: 'bg-blue-500' },
  'in_lavorazione': { label: 'In Lavorazione', color: 'bg-yellow-500' },
  'richiesta_info': { label: 'Richiesta Info', color: 'bg-orange-500' },
  'chiusa': { label: 'Chiusa', color: 'bg-green-500' }
};

const priorityLabels = {
  'bassa': { label: 'Bassa', color: 'bg-gray-500' },
  'media': { label: 'Media', color: 'bg-yellow-500' },
  'alta': { label: 'Alta', color: 'bg-red-500' }
};

export const WhistleblowingView = () => {
  const { reportId } = useParams();
  const [step, setStep] = useState('email'); // email, otp, report
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [messages, setMessages] = useState([]);
  const [accessToken, setAccessToken] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [downloadingIdx, setDownloadingIdx] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [loadingIdentity, setLoadingIdentity] = useState(false);

  const handleRevealIdentity = async () => {
    setLoadingIdentity(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/whistleblowing/reports/${reportId}/identity`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setIdentity(response.data.identity);
      toast.success('Identità del segnalante mostrata: l\'accesso è stato registrato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile mostrare l\'identità del segnalante');
    } finally {
      setLoadingIdentity(false);
    }
  };

  const handleDownloadAttachment = async (idx) => {
    setDownloadingIdx(idx);
    try {
      const response = await axios.get(
        `${API_URL}/api/whistleblowing/reports/${reportId}/attachment-url/${idx}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const url = response.data?.url;
      if (!url) throw new Error('no-url');
      window.open(url.startsWith('http') ? url : `${window.location.origin}${url}`, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error('Impossibile generare il link di download');
    } finally {
      setDownloadingIdx(null);
    }
  };

  // Check for existing token in sessionStorage
  useEffect(() => {
    const savedToken = sessionStorage.getItem(`wb_token_${reportId}`);
    if (savedToken) {
      setAccessToken(savedToken);
      fetchReportWithToken(savedToken);
    }
  }, [reportId]);

  const fetchReportWithToken = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/whistleblowing/report/${reportId}/secure`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data.report);
      setMessages(response.data.messages || []);
      setStep('report');
    } catch (error) {
      // Token expired or invalid
      sessionStorage.removeItem(`wb_token_${reportId}`);
      setAccessToken(null);
      setStep('email');
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Inserisci la tua email');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/whistleblowing/otp/request`, {
        report_id: reportId,
        email: email.trim()
      });
      toast.success('Codice OTP inviato alla tua email');
      setStep('otp');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la richiesta OTP';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode.trim() || otpCode.length !== 6) {
      toast.error('Inserisci un codice OTP valido (6 cifre)');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/whistleblowing/otp/verify`, {
        report_id: reportId,
        email: email.trim(),
        otp_code: otpCode.trim()
      });
      
      // Save token and report
      setAccessToken(response.data.access_token);
      sessionStorage.setItem(`wb_token_${reportId}`, response.data.access_token);
      setReport(response.data.report);
      setMessages(response.data.messages || []);
      setStep('report');
      toast.success('Accesso autorizzato');
    } catch (error) {
      const message = error.response?.data?.detail || 'Codice OTP non valido';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API_URL}/api/whistleblowing/messages`, {
        report_id: reportId,
        sender_type: 'responsabile',
        message: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      // Refresh messages
      await fetchReportWithToken(accessToken);
      setNewMessage('');
      toast.success('Messaggio inviato');
    } catch (error) {
      toast.error('Errore durante l\'invio del messaggio');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen pt-20 bg-background">
      {/* Header */}
      <section className="relative py-12 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto text-center">
            <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
            <h1 className="font-display font-bold text-3xl lg:text-4xl mb-2 text-white">
              Consultazione Segnalazione
            </h1>
            <p className="text-gray-300">
              Accesso protetto con verifica 2FA
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* Step: Email Input */}
            {step === 'email' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="glass-card">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Verifica Identità</CardTitle>
                    <CardDescription>
                      Inserisci la tua email autorizzata per ricevere il codice di accesso
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleRequestOTP} className="space-y-4">
                      <div>
                        <Label htmlFor="email">Email Autorizzata</Label>
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="odv@azienda.it"
                          className="mt-2"
                          data-testid="wb-view-email-input"
                          required
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                          Solo gli indirizzi email OdV o Amministratore configurati possono accedere.
                        </p>
                      </div>
                      
                      <Alert className="border-amber-500/20 bg-amber-500/5">
                        <Lock className="h-4 w-4 text-amber-500" />
                        <AlertDescription className="ml-2 text-sm">
                          Riceverai un codice OTP valido per 10 minuti.
                        </AlertDescription>
                      </Alert>
                      
                      <Button 
                        type="submit" 
                        className="w-full" 
                        size="lg" 
                        disabled={isLoading}
                        data-testid="wb-view-request-otp-btn"
                      >
                        {isLoading ? 'Invio in corso...' : 'Richiedi Codice OTP'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step: OTP Verification */}
            {step === 'otp' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="glass-card">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Key className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Inserisci Codice OTP</CardTitle>
                    <CardDescription>
                      Abbiamo inviato un codice a <strong>{email}</strong>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleVerifyOTP} className="space-y-4">
                      <div>
                        <Label htmlFor="otp">Codice OTP (6 cifre)</Label>
                        <Input
                          id="otp"
                          type="text"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          className="mt-2 text-center text-2xl tracking-widest font-mono"
                          maxLength={6}
                          data-testid="wb-view-otp-input"
                          required
                        />
                      </div>
                      
                      <Alert className="border-red-500/20 bg-red-500/5">
                        <Clock className="h-4 w-4 text-red-500" />
                        <AlertDescription className="ml-2 text-sm">
                          Il codice scade tra 10 minuti. Hai 5 tentativi.
                        </AlertDescription>
                      </Alert>
                      
                      <div className="flex gap-3">
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => setStep('email')}
                        >
                          Indietro
                        </Button>
                        <Button 
                          type="submit" 
                          className="flex-1" 
                          disabled={isLoading || otpCode.length !== 6}
                          data-testid="wb-view-verify-otp-btn"
                        >
                          {isLoading ? 'Verifica...' : 'Verifica Codice'}
                        </Button>
                      </div>
                      
                      <p className="text-center text-sm text-muted-foreground">
                        Non hai ricevuto il codice?{' '}
                        <button 
                          type="button" 
                          onClick={handleRequestOTP}
                          className="text-primary hover:underline"
                          disabled={isLoading}
                        >
                          Invia di nuovo
                        </button>
                      </p>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step: Report View */}
            {step === 'report' && report && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                {/* Report Header */}
                <Card className="glass-card border-primary/20">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <FileText className="w-6 h-6 text-primary" />
                          {report.title}
                        </CardTitle>
                        <CardDescription className="mt-2">
                          Codice: <code className="bg-muted px-2 py-1 rounded">{report.anonymous_code}</code>
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Badge className={`${statusLabels[report.status]?.color || 'bg-gray-500'}`}>
                          {statusLabels[report.status]?.label || report.status}
                        </Badge>
                        <Badge className={`${priorityLabels[report.priority]?.color || 'bg-gray-500'}`}>
                          {priorityLabels[report.priority]?.label || report.priority}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Metadata */}
                    <div className="grid md:grid-cols-3 gap-4 p-4 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">
                          <strong>Categoria:</strong> {reportTypeLabels[report.report_type] || report.report_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">
                          <strong>Data:</strong> {formatDate(report.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">
                          <strong>Anonima:</strong> {report.is_anonymous ? 'Sì' : 'No'}
                        </span>
                      </div>
                      {report.reporter_role && (
                        <div className="flex items-center gap-2" data-testid="report-reporter-role">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">
                            <strong>Ruolo segnalante:</strong> {roleLabels[report.reporter_role] || report.reporter_role}
                          </span>
                        </div>
                      )}
                    </div>

                    {!report.is_anonymous && (
                      <div className="p-4 rounded-lg border border-border bg-muted/30" data-testid="report-identity-box">
                        <p className="text-sm font-medium mb-2">Identità del segnalante (segnalazione nominativa)</p>
                        {identity ? (
                          <div className="text-sm space-y-1" data-testid="report-identity-values">
                            <p><strong>Nome:</strong> {identity.first_name} {identity.last_name}</p>
                            {identity.phone && <p><strong>Telefono:</strong> {identity.phone}</p>}
                            {identity.email && <p><strong>Email:</strong> {identity.email}</p>}
                          </div>
                        ) : (
                          <>
                            <p className="text-xs text-muted-foreground mb-3">
                              I dati sono cifrati a riposo. La consultazione è consentita solo al gestore autorizzato e
                              viene registrata in un log di accesso.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleRevealIdentity}
                              disabled={loadingIdentity}
                              data-testid="reveal-identity-btn"
                            >
                              <Lock className="w-4 h-4 mr-2" />
                              {loadingIdentity ? 'Decifratura...' : 'Mostra identità del segnalante'}
                            </Button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Descrizione
                      </h4>
                      <div className="p-4 rounded-lg bg-muted/30 whitespace-pre-wrap">
                        {report.description}
                      </div>
                    </div>

                    {/* Additional Details */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {report.incident_date && (
                        <div className="p-3 rounded-lg bg-muted/30">
                          <p className="text-sm text-muted-foreground">
                            {report.incident_date_to ? 'Periodo dell\'evento' : 'Data Evento'}
                          </p>
                          <p className="font-medium" data-testid="report-incident-date">
                            {report.incident_date_to
                              ? `dal ${report.incident_date} al ${report.incident_date_to}`
                              : report.incident_date}
                          </p>
                        </div>
                      )}
                      {report.incident_location && (
                        <div className="p-3 rounded-lg bg-muted/30">
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Luogo
                          </p>
                          <p className="font-medium">{report.incident_location}</p>
                        </div>
                      )}
                      {report.received_at && (
                        <div className="p-3 rounded-lg bg-muted/30" data-testid="report-received-at">
                          <p className="text-sm text-muted-foreground">Ricevuta il</p>
                          <p className="font-medium">{formatDate(report.received_at)}</p>
                        </div>
                      )}
                      {report.response_due_at && (
                        <div className="p-3 rounded-lg bg-muted/30" data-testid="report-response-due">
                          <p className="text-sm text-muted-foreground">Riscontro entro (D.Lgs. 24/2023)</p>
                          <div className="font-medium flex flex-wrap items-center gap-2">
                            {formatDate(report.response_due_at)}
                            {report.response_status === 'riscontro_inviato' ? (
                              <Badge className="bg-green-600">Riscontro inviato</Badge>
                            ) : new Date(report.response_due_at) < new Date() ? (
                              <Badge className="bg-red-600">Scaduto</Badge>
                            ) : (
                              <Badge className="bg-yellow-600">In attesa di riscontro</Badge>
                            )}
                          </div>
                        </div>
                      )}
                      {report.persons_involved && (
                        <div className="p-3 rounded-lg bg-muted/30 md:col-span-2">
                          <p className="text-sm text-muted-foreground">Persone Coinvolte</p>
                          <p className="font-medium whitespace-pre-wrap">{report.persons_involved}</p>
                        </div>
                      )}
                      {report.witnesses && (
                        <div className="p-3 rounded-lg bg-muted/30 md:col-span-2">
                          <p className="text-sm text-muted-foreground">Testimoni</p>
                          <p className="font-medium whitespace-pre-wrap">{report.witnesses}</p>
                        </div>
                      )}
                      {(report.good_faith_declared || report.privacy_ack || report.notice_ack) && (
                        <div className="p-3 rounded-lg bg-muted/30 md:col-span-2" data-testid="report-declarations">
                          <p className="text-sm text-muted-foreground mb-1">Dichiarazioni rese all'invio</p>
                          <ul className="text-sm space-y-1">
                            {report.good_faith_declared && (
                              <li>Buona fede dichiarata il {formatDate(report.good_faith_declared_at)}</li>
                            )}
                            {report.notice_ack && (
                              <li>Presa visione informativa whistleblowing il {formatDate(report.notice_ack_at)}</li>
                            )}
                            {report.privacy_ack && (
                              <li>Presa visione informativa privacy il {formatDate(report.privacy_ack_at)}</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {report.attachments && report.attachments.length > 0 && (
                        <div className="p-3 rounded-lg bg-muted/30 md:col-span-2">
                          <p className="text-sm text-muted-foreground mb-2">Allegati</p>
                          <div className="flex flex-wrap gap-2">
                            {report.attachments.map((_, idx) => (
                              <Button
                                key={idx}
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadAttachment(idx)}
                                disabled={downloadingIdx === idx}
                                data-testid={`download-attachment-${idx}`}
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                {downloadingIdx === idx ? 'Attendere...' : `Allegato ${idx + 1}`}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Messages Section */}
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" /> Comunicazioni
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Message List */}
                    {messages.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {messages.map((msg) => (
                          <div 
                            key={msg.id} 
                            className={`p-4 rounded-lg ${
                              msg.sender_type === 'responsabile' 
                                ? 'bg-primary/10 ml-8' 
                                : 'bg-muted/50 mr-8'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <Badge variant="outline">
                                {msg.sender_type === 'responsabile' ? 'Responsabile' : 'Segnalante'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(msg.created_at)}
                              </span>
                            </div>
                            <p className="whitespace-pre-wrap">{msg.message}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">
                        Nessuna comunicazione presente
                      </p>
                    )}

                    {/* New Message Input */}
                    <div className="pt-4 border-t border-border">
                      <Label htmlFor="newMessage">Invia un messaggio al segnalante</Label>
                      <Textarea
                        id="newMessage"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Scrivi un messaggio..."
                        rows={3}
                        className="mt-2"
                      />
                      <Button 
                        onClick={handleSendMessage} 
                        className="mt-3"
                        disabled={!newMessage.trim() || sendingMessage}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {sendingMessage ? 'Invio...' : 'Invia Messaggio'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Session Info */}
                <Alert className="border-green-500/20 bg-green-500/5">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription className="ml-2 text-sm">
                    Sessione attiva. L'accesso scade automaticamente dopo 1 ora di inattività.
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhistleblowingView;
